import { useEffect, useMemo, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    expandAction,
    useDataTable,
    useDataTableExpansion,
    visibleColumns,
    type DataTableAction,
    type DataTableFilter,
    type DataTableRefs
} from '@odo/shared-ui';
import { useInfiniteInvoices, useInvoices, usePracticeOptions } from '../../../hooks/useBilling';
import { useMyPermissions } from '../../../hooks/useMyPermissions';
import { DATA_TABLE_ROW_HEIGHT } from '../../../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../../../hooks/useViewportTableLimit';
import { ChargeDrawer } from '../ChargeDrawer';
import { InvoiceDrawer, VoidInvoiceModal } from '../InvoiceDrawer';
import { InvoiceStatusCards } from '../InvoiceStatusCards';
import { BILLING_LIST_SEARCH_MAX_LENGTH, type InvoiceListItem } from '../../../services/billing';
import { buildInvoiceColumns, type InvoicesSortBy } from './invoiceColumns';
import { ExportInvoicesModal } from './ExportInvoicesModal';

// The full invoices experience -- status cards, search/filters/sort, table,
// pagination, and drawer hosting (invoice <-> charge drawer swap + void
// modal) -- shared by the org-wide InvoicesList page and the Accounts
// practice detail Invoices tab. `practiceId` scopes the query and hides the
// (now redundant) practice filter + column; omitted means org-wide, same as
// today.

const SEARCH_DEBOUNCE_MS = 300;
const STATUS_OPTIONS = [
    'pending',
    'created',
    'open',
    'partial',
    'overdue',
    'paid',
    'error',
    'void'
] as const;
// Fixed page size for the fillHeight (embedded) layout -- useViewportPageSize
// measures against the full window, which isn't meaningful inside a
// constrained pane, so that mode scrolls its own rows instead.
const FILL_HEIGHT_PAGE_SIZE = 15;
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

// A type alias, not an interface: only aliases pick up TypeScript's implicit
// index signature, which is what lets this satisfy `DataTableFilterMap`.
type InvoiceFilters = { status: string[]; practiceId: string[] };

// Soonest due first, biggest amount first, newest creation first, patient name
// A-Z first -- the order applied when sorting moves to a different column.
const defaultOrderFor = (by: InvoicesSortBy) =>
    by === 'due' || by === 'patient' ? ('asc' as const) : ('desc' as const);

export interface InvoicesContentProps {
    // Scopes the list to one practice and hides the practice filter + column;
    // omitted means org-wide.
    practiceId?: string;
    // Invoice-drawer state is pluggable: the org page drives it from the route
    // (deep-linkable), an embedded instance uses local state.
    openInvoiceId: string | null;
    onOpenInvoice: (id: string) => void;
    onCloseInvoice: () => void;
    statusFilterStorageKey: string;
    practiceFilterStorageKey: string;
    // Switches from the org page's viewport-fit layout (no internal scroll,
    // page size shrinks/grows to the window) to a fixed-height panel with its
    // own internal scroll region and pinned pagination, for embedding in a
    // constrained pane.
    fillHeight?: boolean;
    // Fires with the "All invoices" status-card count so a host page can
    // mirror it into its own page header subtitle (e.g. "N total").
    onTotalCountChange?: (count: number) => void;
}

export function InvoicesContent({
    practiceId,
    openInvoiceId,
    onOpenInvoice,
    onCloseInvoice,
    statusFilterStorageKey,
    practiceFilterStorageKey,
    fillHeight = false,
    onTotalCountChange
}: InvoicesContentProps) {
    const { hasPermission } = useMyPermissions();
    const canEditBilling = hasPermission('billing.update');

    // Page, limit, sort, search and both filters -- plus the "any of those
    // changed, go back to page 1" rule and the sessionStorage persistence this
    // component used to hand-roll -- all live in the hook now.
    const table = useDataTable<InvoicesSortBy, InvoiceFilters>({
        defaultSort: { by: 'date', order: 'desc' },
        defaultLimit: fillHeight ? FILL_HEIGHT_PAGE_SIZE : VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { status: [], practiceId: [] },
        defaultOrderFor,
        // Same two keys, same bare-JSON-array format as before, so a user's
        // stored filters survive this migration. Sort is deliberately not
        // persisted (it never was), which is why there is no `storageKey`.
        // A practice-scoped page hides the practice filter, so it stops
        // persisting one too. Mode is deliberately not persisted either, for
        // the same reason sort never was - it stays session-only, defaulting
        // to `useDataTable`'s 'infinite' every visit.
        filterStorageKeys: practiceId
            ? { status: statusFilterStorageKey }
            : { status: statusFilterStorageKey, practiceId: practiceFilterStorageKey }
    });
    const { status: statusFilter, practiceId: practiceFilter } = table.filters;
    const isInfinite = table.mode === 'infinite';

    // Full-screen expansion only makes sense for the embedded (fillHeight)
    // pane - the standalone billing page is already a full page. The hook is
    // still called unconditionally (rules of hooks); only the `expansion`
    // prop below is gated.
    const expansion = useDataTableExpansion();

    const [search, setSearch] = useState('');
    const [openChargeId, setOpenChargeId] = useState<string | null>(null);
    const [voidTarget, setVoidTarget] = useState<InvoiceListItem | null>(null);
    const [showExport, setShowExport] = useState(false);

    // Debouncing stays here on purpose: the hook holds committed state, and
    // what counts as "committed" is the app's call. `setSearch` resets the page.
    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search, commitSearch]);

    const viewport = useViewportTableLimit(table, {
        rowHeight: DATA_TABLE_ROW_HEIGHT,
        fixedPageSize: fillHeight ? FILL_HEIGHT_PAGE_SIZE : undefined
    });

    // `table.params` is the canonical bag: { page, limit, sort, order, search?,
    // status?, practiceId? }. `/v2/billing/invoices` agrees on all of it bar
    // `search`, which it spells `q`. That rename happens here rather than
    // through `adaptDataTableParams` because `fetchInvoices` takes a typed
    // param object and owns the serialisation itself; the adapter is for
    // endpoints handed a raw query bag.
    const { page, limit, sort, order } = table.params;
    const sharedInvoiceParams = {
        limit,
        q: table.search || undefined,
        status: statusFilter[0],
        practiceId: practiceId ?? practiceFilter[0],
        sort,
        order
    };
    // Both hooks are called unconditionally (React's rules of hooks) and only
    // one is ever `enabled` - the other sits idle rather than fetching in the
    // background. Each carries its own query key, so switching `mode` never
    // appends one's rows onto the other's; it just starts reading whichever
    // side already has (or now fetches) its own cache entry.
    const paged = useInvoices({
        ...sharedInvoiceParams,
        page,
        enabled: !isInfinite && viewport.isReady
    });
    const infinite = useInfiniteInvoices({ ...sharedInvoiceParams, enabled: isInfinite });

    const invoices = isInfinite ? infinite.invoices : paged.invoices;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isFetching = isInfinite ? infinite.isFetching : paged.isFetching;

    const { practices } = usePracticeOptions();
    const totalPages = Math.max(1, Math.ceil(meta.total / limit));

    const statusSummary = meta.statusSummary ?? {};
    // 'overdue' overlaps the stored statuses, so it stays out of the All total.
    const allCount = Object.entries(statusSummary)
        .filter(([key]) => key !== 'overdue')
        .reduce((sum, [, entry]) => sum + entry.count, 0);

    useEffect(() => {
        onTotalCountChange?.(allCount);
    }, [allCount, onTotalCountChange]);

    const practiceOptions = useMemo(
        () =>
            practices.map((p) =>
                p.doctorName
                    ? {
                          value: p.id,
                          label: p.name,
                          sublabel: `Dr. ${p.doctorName}`,
                          keywords: [`Dr. ${p.doctorName}`, p.doctorName]
                      }
                    : { value: p.id, label: p.name }
            ),
        [practices]
    );

    // Status keeps `workQueueVariant`/`align="right"`/`searchable` (its 8
    // options sit right at the shared dropdown's >8 auto-search threshold, so
    // the search box would otherwise silently disappear) to match this pill's
    // pre-migration look exactly. Practice is scoped out entirely on a
    // practice-scoped page rather than rendered disabled.
    const filters: DataTableFilter[] = [
        {
            key: 'status',
            label: 'Status',
            multiple: true,
            options: STATUS_OPTIONS as unknown as string[],
            value: statusFilter,
            onChange: (value) => table.setFilter('status', value),
            workQueueVariant: true,
            align: 'right',
            searchable: true
        },
        ...(practiceId
            ? []
            : [
                  {
                      key: 'practiceId',
                      label: 'Practice',
                      multiple: true,
                      options: practiceOptions,
                      value: practiceFilter,
                      onChange: (value) => table.setFilter('practiceId', value),
                      workQueueVariant: true,
                      align: 'right',
                      searchable: true
                  } satisfies DataTableFilter
              ])
    ];

    // The charge drawer overlays the invoice drawer; while an invoice is open,
    // the charge drawer gets a back chevron returning to it.
    const openCharge = (chargeId: string) => setOpenChargeId(chargeId);
    const closeCharge = () => {
        setOpenChargeId(null);
        if (openInvoiceId) onCloseInvoice();
    };

    const showPracticeColumn = !practiceId;

    const columns = useMemo(
        () =>
            buildInvoiceColumns({
                showPracticeColumn,
                canEdit: canEditBilling,
                onOpenCharge: openCharge,
                onVoid: setVoidTarget
            }),
        [showPracticeColumn, canEditBilling]
    );

    const refs: DataTableRefs | undefined = fillHeight
        ? undefined
        : {
              card: viewport.tableCardRef,
              head: viewport.tableHeaderRef,
              body: viewport.tableBodyRef,
              pagination: viewport.paginationRef
          };

    return (
        <div
            className={
                fillHeight
                    ? 'flex flex-col h-full min-h-0'
                    : 'flex flex-col h-full overflow-hidden bg-gray-100'
            }
        >
            {/* Status filter cards */}
            <div className="shrink-0">
                <InvoiceStatusCards
                    statusSummary={statusSummary}
                    selected={statusFilter}
                    onChange={(value) => table.setFilter('status', value)}
                    className={fillHeight ? '' : undefined}
                />
            </div>

            {/* Table card, with the search/filters/expand toolbar generated by
                DataTable itself (see `search`/`filters`/`toolbar`/`actions`
                below) so it renders as one row inside the card - the expand
                toggle previously landed on its own generated-toolbar row
                because this toolbar lived outside the table. */}
            <DataTable
                label="Invoices"
                columns={columns}
                rows={invoices}
                getRowKey={(invoice) => invoice.id}
                rowAction={{ onActivate: (invoice) => onOpenInvoice(invoice.id), mode: 'click' }}
                rowClassName={(invoice) =>
                    invoice.status === 'error' ? 'bg-red-50/40' : undefined
                }
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode={table.mode}
                onModeChange={table.setMode}
                pagination={{ page, totalPages, total: meta.total, onPage: table.setPage }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                isLoading={isLoading}
                loadingRows={
                    <tr>
                        <td
                            colSpan={visibleColumns(columns).length}
                            className="px-3 py-8 text-center"
                        >
                            <div className="flex items-center justify-center gap-2 text-gray-400">
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                                <span className="text-sm">Loading invoices…</span>
                            </div>
                        </td>
                    </tr>
                }
                emptyState="No invoices found."
                refs={refs}
                className={fillHeight ? 'overflow-hidden' : 'overflow-hidden px-8 pb-8'}
                search={{
                    value: search,
                    onChange: setSearch,
                    placeholder: practiceId
                        ? 'Search invoice # or patient'
                        : 'Search invoice #, practice, or patient',
                    maxLength: BILLING_LIST_SEARCH_MAX_LENGTH
                }}
                filters={filters}
                // "Refreshing…" rides the toolbar slot - it lands between the
                // filter pills and the expand toggle, the one open position in
                // generated-toolbar order (see DataTableProps.toolbar).
                toolbar={
                    isFetching && !isLoading ? (
                        <span className="text-xs text-gray-400">Refreshing…</span>
                    ) : undefined
                }
                // The toolbar is generated by these props, so it lives inside
                // the table and rides along into the expand overlay. Export is
                // always offered; the expand toggle stays fillHeight-only.
                actions={
                    [
                        {
                            key: 'export',
                            label: 'Export',
                            icon: <ArrowDownTrayIcon className="h-4 w-4" />,
                            onClick: () => setShowExport(true)
                        },
                        ...(fillHeight ? [expandAction(expansion, { label: 'Invoices' })] : [])
                    ] satisfies DataTableAction[]
                }
                expansion={fillHeight ? expansion : undefined}
            />

            {showExport && (
                <ExportInvoicesModal
                    practiceId={practiceId}
                    // Same practice the table is showing, so Export doesn't
                    // silently widen an admin's filtered view back to all.
                    initialPracticeId={practiceFilter[0]}
                    onClose={() => setShowExport(false)}
                />
            )}

            {voidTarget && (
                <VoidInvoiceModal invoice={voidTarget} onClose={() => setVoidTarget(null)} />
            )}

            {openInvoiceId && !openChargeId && (
                <InvoiceDrawer
                    invoiceId={openInvoiceId}
                    onClose={onCloseInvoice}
                    onOpenCharge={openCharge}
                />
            )}

            {openChargeId && (
                <ChargeDrawer
                    chargeId={openChargeId}
                    onClose={closeCharge}
                    onBack={openInvoiceId ? () => setOpenChargeId(null) : undefined}
                />
            )}
        </div>
    );
}
